/**
 * The BUBBLE BOBBLE logo (08 §1), after the arcade title's: big, fat, glossy bubble letters, BUBBLE
 * over BOBBLE. Each letter is a smooth surface round its strokes' centre lines (logoGlyphs.ts),
 * blended where strokes meet, polygonised once per distinct letter (logoSurface.ts) and shared by
 * every copy. Yellow, with one white glint on each letter's crown where the key light would catch it.
 * Each letter is its own mesh, pivoting at its centre, so the logo can pop in and bob letter by letter.
 */

import { type BufferGeometry, type Color, Group, type Material, Mesh, Vector3 } from 'three';
import type { Palette } from '../palette';
import { LOGO_STROKE, logoGlyph, strokeClosest } from './logoGlyphs';
import { type Field, type Gradient, surfaceNets } from './logoSurface';

/** The two lines of the logo. */
export const LOGO_ROWS = ['BUBBLE', 'BOBBLE'] as const;

/** Front-to-back squash of the strokes' round section (1 = round tubes). */
const DEPTH = 0.82;
/** How much the strokes of a letter swell into each other where they meet (glyph units). */
const BLEND = 0.07;
/** Grid cell of the surface, glyph units: about a texel at the logo's size on a 1280-wide frame. */
const CELL = 0.018;
/**
 * Half-way between the key light (from the upper front left, grades/stage.json) and the camera: a
 * surface facing this way shows the light's glint.
 */
const GLINT = new Vector3(-0.16, 0.54, 0.83).normalize();

/** Polynomial smooth minimum: min(a, b) with the corner rounded over `k`. */
function smin(a: number, b: number, k: number): number {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - h * h * k * 0.25;
}

/**
 * The field of letter `ch`, centred on its box (glyph units): the strokes' round sections, each the
 * distance from its centre line with z stretched by 1 / DEPTH, joined by the smooth minimum; and its
 * exact gradient (the smooth minimum hands each side a share of its slope: 1 - h/2 to the nearer
 * stroke, h/2 to the farther), so the normals cost one pass over the strokes, not six.
 */
export function letterField(ch: string): { field: Field; gradient: Gradient } {
  const g = logoGlyph(ch);
  const cx = g.width / 2;
  const near: [number, number] = [0, 0];
  // The distances in the glyph's plane do not depend on z: keep the last point's, so a run of samples
  // down one column (as surfaceNets takes them) measures the strokes once.
  const planar = new Float64Array(g.strokes.length);
  let lastX = NaN;
  let lastY = NaN;
  const field: Field = (x, y, z) => {
    if (x !== lastX || y !== lastY) {
      g.strokes.forEach((s, i) => { planar[i] = strokeClosest(s, x + cx, y + 0.5, near); });
      lastX = x;
      lastY = y;
    }
    const zz = z / DEPTH;
    let d = Infinity;
    for (const p of planar) {
      const ds = Math.sqrt(p * p + zz * zz);
      d = d === Infinity ? ds : smin(d, ds, BLEND);
    }
    return d - LOGO_STROKE;
  };
  const one = new Vector3();
  const gradient: Gradient = (x, y, z, out) => {
    const px = x + cx;
    const py = y + 0.5;
    const zz = z / DEPTH;
    let d = Infinity;
    for (const s of g.strokes) {
      const p = strokeClosest(s, px, py, near);
      const ds = Math.max(1e-9, Math.sqrt(p * p + zz * zz));
      one.set((px - near[0]) / ds, (py - near[1]) / ds, zz / (DEPTH * ds));
      if (d === Infinity) {
        out.copy(one);
      } else {
        const h = Math.max(BLEND - Math.abs(d - ds), 0) / BLEND;
        const [mine, theirs] = d <= ds ? [1 - h / 2, h / 2] : [h / 2, 1 - h / 2];
        out.multiplyScalar(mine).addScaledVector(one, theirs);
      }
      d = d === Infinity ? ds : smin(d, ds, BLEND);
    }
    return out;
  };
  return { field, gradient };
}

/** Letter `ch` as a smooth mesh one glyph unit tall (plus its stroke), centred on the origin. */
export function buildLetter(p: Palette, ch: string): BufferGeometry {
  const g = logoGlyph(ch);
  const pad = LOGO_STROKE + CELL * 3;
  const box = {
    min: new Vector3(-g.width / 2 - pad, -0.5 - pad, -LOGO_STROKE * DEPTH - CELL * 3),
    max: new Vector3(g.width / 2 + pad, 0.5 + pad, LOGO_STROKE * DEPTH + CELL * 3),
  };
  const body = p.color('bubTrim.1');
  const glint = p.color('paper.0');
  // The glint only on the letter's upper part: inside a counter the same slope would catch it too, and
  // spots there read as mess, not gloss.
  const { field, gradient } = letterField(ch);
  const paint = (n: Vector3, at: Vector3): Color => (n.dot(GLINT) > 0.965 && at.y > 0.2 ? glint : body);
  return surfaceNets(field, box, CELL, paint, gradient);
}

export interface Logo {
  readonly root: Group;
  /** The letters in reading order, BUBBLE then BOBBLE; each at its rest place (`userData.rest`). */
  readonly letters: readonly Mesh[];
  /** Built geometries, one per distinct letter. */
  readonly geometries: readonly BufferGeometry[];
}

export interface LogoLayout {
  /** Full height of a letter, tiles. */
  letterHeight: number;
  /** Centre y of each row, tiles. */
  rowY: readonly [number, number];
  /** Space between letters, glyph units (negative: they overlap, as bubble letters do). */
  gap: number;
}

export const LOGO_LAYOUT: LogoLayout = { letterHeight: 5, rowY: [9.9, 4.9], gap: -0.03 };

/** A little jaunty play in each letter's seat, so the rows read as blown bubbles, not type. */
const TILT = [0.05, -0.04, 0.03, -0.05, 0.04, -0.03];
const LIFT = [0.1, -0.08, 0.06, -0.04, 0.08, -0.1];

export function buildLogo(p: Palette, material: Material, layout: LogoLayout = LOGO_LAYOUT): Logo {
  const root = new Group();
  const scale = layout.letterHeight / (1 + 2 * LOGO_STROKE);
  const cache = new Map<string, BufferGeometry>();
  const letters: Mesh[] = [];
  LOGO_ROWS.forEach((word, row) => {
    const widths = [...word].map((ch) => logoGlyph(ch).width + 2 * LOGO_STROKE);
    const total = widths.reduce((a, w) => a + w, 0) + layout.gap * (word.length - 1);
    let x = -total / 2;
    [...word].forEach((ch, i) => {
      let geometry = cache.get(ch);
      if (!geometry) {
        geometry = buildLetter(p, ch);
        cache.set(ch, geometry);
      }
      const w = widths[i] ?? 0;
      const mesh = new Mesh(geometry, material);
      mesh.scale.setScalar(scale);
      const rest = new Vector3((x + w / 2) * scale, (layout.rowY[row] ?? 0) + (LIFT[i] ?? 0), 0);
      mesh.position.copy(rest);
      mesh.rotation.z = TILT[(i + row) % TILT.length] ?? 0;
      mesh.userData['rest'] = rest;
      mesh.userData['tilt'] = mesh.rotation.z;
      mesh.userData['scale'] = scale;
      letters.push(mesh);
      root.add(mesh);
      x += w + layout.gap;
    });
  });
  return { root, letters, geometries: [...cache.values()] };
}

/** Overshoot ease: 0 -> 1, passing 1 and settling back, as a bubble blown up. */
function popIn(u: number): number {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  const c = 2.2;
  const v = u - 1;
  return 1 + (c + 1) * v * v * v + c * v * v;
}

/** When letter i starts to pop in, seconds after the title appears. */
export const logoPopAt = (i: number): number => 0.1 + i * 0.06;
/** How long a letter takes to pop in, seconds. */
export const LOGO_POP_SECONDS = 0.45;
/** The gentle bob's height (tiles) either way of rest. */
export const LOGO_BOB = 0.16;

/**
 * The logo at `t` seconds since the title appeared: the letters pop in one after another, then bob
 * gently in a wave running along each row, rocking a little. A continuous function of t.
 */
export function poseLogo(logo: Logo, t: number): void {
  logo.letters.forEach((m, i) => {
    const rest = m.userData['rest'];
    const tilt = m.userData['tilt'];
    const scale = m.userData['scale'];
    if (!(rest instanceof Vector3) || typeof tilt !== 'number' || typeof scale !== 'number') return;
    const s = popIn((t - logoPopAt(i)) / LOGO_POP_SECONDS);
    m.visible = s > 0.001;
    m.scale.setScalar(scale * s);
    const k = i % 6;
    const phase = t * Math.PI * 2 * 0.45 - k * 0.6;
    m.position.set(rest.x, rest.y + Math.sin(phase) * LOGO_BOB, rest.z);
    m.rotation.z = tilt + Math.sin(t * Math.PI * 2 * 0.3 + i * 1.3) * 0.03;
  });
}
